import React, {useEffect, useMemo, useState} from 'react';
import {Button} from '@finpay-development/shared-components';
import {
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import PersonIcon from '@mui/icons-material/Person';
import {useFormik} from 'formik';
import * as Yup from 'yup';
import {
  currentInsuranceInfo,
  estimatesPatientInfo, financialAssistanceInfo,
} from '@finpay-development/shared-platform/lib/esm/shared/validation/schemas';
import {PatientFormValues} from './types';
import {usePatientForm} from './hooks/usePatientForm';
import {useCurrentInsurancePayors} from './hooks/useCurrentInsurancePayors';
import {useRunAvailityVob} from './hooks/useRunAvailityVob';
import {PersonalInfoSection} from './sections/personal-info';
import {ContactInfoSection} from './sections/contact-info';
import {AddressInfoSection} from './sections/address-info';
import {EmploymentInfoSection} from './sections/employment-info';
import CustomizeableAccordion
  from 'src/shared/components/customizeable-accordion';
import {InsuranceInfoSection} from './sections/current-insurance-info';
import {useDispatch, useSelector} from 'react-redux';
import {AppDispatch} from '../../../../shared/state/store';
import {showErrorStatus} from '@finpay-development/shared-platform/lib/esm/security/state/user-slice';
import {RootState} from '../../../../shared/state/root-reducer';
import {useNavigate} from 'react-router-dom';
import {
  emptyPatientWaiver,
  PatientCurrentInsurance,
} from '@finpay-development/shared-platform/lib/esm/patient/components/models/patient-view-model';
import {userService} from '@finpay-development/shared-platform/lib/esm/admin/services/admin-user-service';
import {
  savePatient,
  savePatientCurrentInsurance, savePatientWaiver,
} from '@finpay-development/shared-platform/lib/esm/patient/state/patient-thunk';
import {stateAbbrevLookup} from '@finpay-development/shared-platform/lib/esm/shared/misc/us-states';
import {State} from '@finpay-development/shared-platform/lib/esm/shared/model/state';
import {PrimaryAddress} from '@finpay-development/shared-platform/lib/esm/shared/model/primary-address';
import {
  ContactIncome, emptyContactIncome,
  PhoneType, PrimaryContact,
} from '@finpay-development/shared-platform/lib/esm/shared/model/primary-contact';
import {Utils} from '@finpay-development/shared-platform/lib/esm/shared/utils';
import AddressValidator from '../../../../shared/components/address-validator';
import { clearPatient } from '@finpay-development/shared-platform/lib/esm/admissions-advisor/state/vob-patient-slice';
import { clearPatientVobList, clearSelectedVob } from '@finpay-development/shared-platform/lib/esm/shared/state/vob-slice';
import { clearSavedEstimatesList, clearSelectedEstimate } from '@finpay-development/shared-platform/lib/esm/shared/state/estimator-slice';
import { clearFacilityPayers } from '@finpay-development/shared-platform/lib/esm/admissions-advisor/state/vob-slice';
import { clearRiskAssessment } from '@finpay-development/shared-platform/lib/esm/admissions-advisor/state/risk-assessment-slice';
import { clearSelectedEncounter, clearComments, setPatientWaiver } from '@finpay-development/shared-platform/lib/esm/patient/state/patient-slice';
import { resolveWaiverIdForSave } from './utils/resolve-waiver-id-for-save';
import _ from 'lodash';
import {
  admissionsAdvisorService
} from '@finpay-development/shared-platform/lib/esm/admissions-advisor/services/admissions-advisor-services';
import {PowerlyticsData,} from '@finpay-development/shared-platform/lib/esm/admissions-advisor/state/vob-patient-thunk';
import {FinancialAssistance} from './sections/financial-assistance';
import {patientService} from '@finpay-development/shared-platform/lib/esm/patient/services/patient-service';
import moment from 'moment';
import {UpdateInsuranceAfterVobDialog} from './sections/UpdateInsuranceAfterVobDialog';
import {SelectFacilityForAvailityDialog} from './sections/SelectFacilityForAvailityDialog';

interface PatientFormProps {
  onFormValuesChange?: (values: {
    clientId?: number;
    clientEmployerId?: number | null;
    payorId?: number | null;
    groupNumber?: string;
    memberId?: string;
  }) => void;
  onAvailityRunningChange?: (isRunning: boolean) => void;
  onAvailityCancelReady?: (cancel: () => void) => void;
}
export const PatientForm = ({
  onFormValuesChange,
  onAvailityRunningChange,
  onAvailityCancelReady,
}: PatientFormProps) => {

  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const {initialValues} = usePatientForm();

  const [isSaving, setIsSaving] = useState(false);

  const formik = useFormik({
    initialValues,
    validate: async (values: PatientFormValues) => {
      const errors = {};
      let message = `Please resolve the following issues: `;
      try {
        await Yup.object({
          ...estimatesPatientInfo,
          ...currentInsuranceInfo,
          ...financialAssistanceInfo
        }).validate(values, {abortEarly: false});
      } catch (err: any) {
        err.inner.forEach((error: any) => {
          // @ts-ignore
          errors[error.path] = error.message;
          message += ` ${error.message};`;
        });

        if (shouldShowErrorMessage) {
          dispatch(showErrorStatus(message));
          shouldShowErrorMessage = false;
        }
      }
      return errors;
    },
    onSubmit: () => {
    },
    enableReinitialize: !isSaving,
  });

  const allClientsMap = useSelector((state: RootState) => {
    return state.implementationContext?.implementationSpecialistClient.allClientsWithFacilitiesMap;
  });
  const clients = useSelector(
      (state: RootState) =>
          state.implementationContext.implementationSpecialistClient.filteredByIsActive,
  );
  const clientIsLoading = useSelector(
      (state: RootState) =>
          state.implementationContext.implementationSpecialistClient.isLoading,
  );
  const clientIsError = useSelector(
      (state: RootState) =>
          state.implementationContext.implementationSpecialistClient.isError.client,
  );
  const patientContextIsLoading = useSelector((state: RootState) => {
    return state.patientContext.isLoading
  });
  const patientContextIsError = useSelector((state: RootState) => {
    return state.patientContext.isError
  });
  const metadataContextIsLoading = useSelector((state: RootState) => {
    return state.metaData.isLoading
  });
  const metadataContextIsError = useSelector((state: RootState) => {
    return state.metaData.isError
  });
  const employmentStatus = useSelector(
      (state: RootState) => {
        return state.metaData.employmentStatus;
      },
  );
  const clientWaiversMap = useSelector(
      (state: RootState) => {
        return state.patientContext.clientWaiversMap;
      },
  );
  const clientScholarshipsConfig= useSelector(
      (state: RootState) =>
          state.adminContext.adminConfigurationContext?.scholarships
  )
  const selectedPatientWaiver = useSelector(
      (state: RootState) => state.patientContext.selectedPatientWaiver
  )

  const { payors: currentInsurancePayors, isLoading: insurancePayorsListLoading } =
      useCurrentInsurancePayors(formik.values.clientId, formik.values.patientId);

  let shouldShowErrorMessage = false;

  //Used to keep track if values have updated since last Run Capacity to Pay call
  const [cTPInitialValues, setCTPInitialValues] = useState({
    zipCode: initialValues.zipCode,
    zipCode4: initialValues.zipCode4,
    yearlySalary: initialValues.yearlySalary
  })

  //Used to keep track if values have updated since last Waiver Eligibility call
  const [wEInitialValues, setWEInitialValues] = useState({
        clientId: initialValues.clientId,
        householdSize: initialValues.householdSize,
        state: initialValues.state,
        yearlySalary : initialValues.yearlySalary,
        monthlyExpenses : initialValues.monthlyExpenses
  })

  useEffect(()=>{
    //Need to update useState after api calls
    setCTPInitialValues({
      zipCode: initialValues.zipCode,
      zipCode4: initialValues.zipCode4,
      yearlySalary: initialValues.yearlySalary
    })
    setWEInitialValues({
      clientId: initialValues.clientId,
      householdSize: initialValues.householdSize,
      state: initialValues.state,
      yearlySalary : initialValues.yearlySalary,
      monthlyExpenses : initialValues.monthlyExpenses
    })
  }, [initialValues.clientId, initialValues.zipCode, initialValues.zipCode4, initialValues.yearlySalary, initialValues.householdSize, initialValues.state, initialValues.monthlyExpenses])

  // auto-fills the Client field when there is exactly one client and the form doesn’t already 
  // have a client chosen
  useEffect(() => {
    if (clientIsLoading || !clients || clients.length !== 1) {
      return;
    }
    const onlyId = clients[0].clientId;
    if (onlyId == null) {
      return;
    }
    const cid = formik.values.clientId;
    if (cid && cid > 0) {
      return;
    }
    formik.setFieldValue('clientId', onlyId);
    formik.setFieldValue('clientEmployerId', emptyContactIncome.clientEmployerId);
    formik.setFieldValue('fplRatio', emptyPatientWaiver.fplRatio);
    formik.setFieldValue('expenseIncomeRatio', emptyPatientWaiver.expenseIncomeRatio);
    formik.setFieldValue('scholarshipPercentage', emptyPatientWaiver.scholarshipPercentage);
    formik.setFieldValue('expirationDt', emptyPatientWaiver.expirationDt);
    formik.setFieldValue('selectedScholarshipConfig', null);
  }, [clientIsLoading, clients, formik.values.clientId]);

  let isLoading =
      clientIsLoading ||
      patientContextIsLoading.patientDemographics ||
      patientContextIsLoading.patientCurrentInsurance ||
      metadataContextIsLoading.metadata

  const isPatientTabBlockingLoading =
      isLoading || insurancePayorsListLoading;

  let isLoadingError =
      clientIsError ||
      patientContextIsError.patient ||
      patientContextIsError.patientCurrentInsurance ||
      metadataContextIsError.metadata

  useEffect(()=>{
    if(isLoadingError) {
      dispatch(showErrorStatus("An error occurred while loading the page. Please refresh the page and try again. If the problem persists, contact a FinPay administrator."))
      isLoading = false
      isLoadingError = false
    }
  }, [isLoadingError, dispatch])

  const handleSave = async (
    insuranceOverride?: { payorId: number; payorPlanId: number },
    options?: { skipNavigateOnCreate?: boolean }
  ): Promise<{ ok: true; patientId: number } | { ok: false }> => {

    try {

      let zipCode4;
      let powerlyticsData: PowerlyticsData | {} = {}
      const valuesForSave = insuranceOverride
        ? {
            ...formik.values,
            payorId: insuranceOverride.payorId,
            payorPlanId: insuranceOverride.payorPlanId,
          }
        : formik.values;

      //get zipCode4 if not filled in
      if (!formik.values.zipCode4 && formik.values.zipCode &&
          formik.values.streetAddress1 && formik.values.city &&
          formik.values.state !== "-1") {
        const addressValidator = new AddressValidator();
        const zipCode4Response = await addressValidator.getZipCodePlus4(
            formik.values);
        if (['0', '1'].includes(zipCode4Response)) {
          console.log('Error retrieving Zip Code 4 value');
          //Means the existing Capacity to Pay information is invalid. Need to clear it out
          powerlyticsData = {
            verificationScore: emptyContactIncome.verificationScore,
            capacityToPay: emptyContactIncome.capacityToPay,
            disposableIncome: emptyContactIncome.disposableIncome,
            retirementIncome: emptyContactIncome.retirementIncome,
            businessIncome: emptyContactIncome.businessIncome,
            wealthIncome: emptyContactIncome.wealthIncome,
            wagesAndEarnings: emptyContactIncome.wagesAndEarnings,
          }
        } else {
          zipCode4 = zipCode4Response;
        }
      }

      //get capacity to pay
      if(
          //patient is employed and zipCode, zipCode4, and yearlySalary are filled out
          (selectedEmploymentStatus?.enableCapacityToPay && formik.values.zipCode !== "-1" && formik.values.yearlySalary && (formik.values.zipCode4 || zipCode4)) &&
          //zipCode, zipCode4, yearlySalary have changed since last run
          !_.isEqual(
              cTPInitialValues,
              _.pick(formik.values, ['zipCode', 'zipCode4', 'yearlySalary'])
          )
      ){

        const response = await admissionsAdvisorService.getCapacityToPay(
            -2,
            formik.values.yearlySalary,
            formik.values.zipCode,
            zipCode4 ? zipCode4 : formik.values.zipCode4,
        );

        if(!response.hasErrors){
          powerlyticsData = response.entity?.formatted

        }else{
          //If Capacity to Pay API call fails but the address info is updated, the existing Capacity to Pay information is invalid. Need to clear it out
          powerlyticsData = {
            verificationScore: emptyContactIncome.verificationScore,
            capacityToPay: emptyContactIncome.capacityToPay,
            disposableIncome: emptyContactIncome.disposableIncome,
            retirementIncome: emptyContactIncome.retirementIncome,
            businessIncome: emptyContactIncome.businessIncome,
            wealthIncome: emptyContactIncome.wealthIncome,
            wagesAndEarnings: emptyContactIncome.wagesAndEarnings,
          }
        }
      }

      //Run Waiver Eligibility

      let waiverData;

      if (isClientConfiguredForFA &&
          formik.values.clientId &&
          formik.values.householdSize &&
          formik.values.state !== "-1" &&
          formik.values.yearlySalary &&
          formik.values.monthlyExpenses &&
          !_.isEqual(
              wEInitialValues,
              _.pick(formik.values, ['clientId', 'householdSize', 'state', 'yearlySalary', 'monthlyExpenses'])
          )
      ) {

        const response = await admissionsAdvisorService.getNationalPovertyThreshold(
            -2,
            formik.values.householdSize,
            formik.values.state,
            new Date().getFullYear(),
        );

        if (response.hasErrors) {
          //If Federal Poverty API call fails but the relevant info is updated, the existing waiver information is invalid. Need to clear it out

          waiverData = {
            scholarshipPercentage: emptyPatientWaiver.scholarshipPercentage,
            fplRatio: emptyPatientWaiver.fplRatio,
            expenseIncomeRatio: emptyPatientWaiver.expenseIncomeRatio,
            expirationDt: emptyPatientWaiver.expirationDt
          }

        }else{

          const waiverResponse = await patientService.getPatientWaiverEligibility({
            clientId: formik.values.clientId!,
            povertyThreshold: Number(response.entity.data.poverty_threshold),
            monthlyIncome: formik.values.yearlySalary/12,
            monthlyExpenses: formik.values.monthlyExpenses,
          })

          if (waiverResponse.hasErrors) {
            waiverData = {
              scholarshipPercentage: emptyPatientWaiver.scholarshipPercentage,
              fplRatio: emptyPatientWaiver.fplRatio,
              expenseIncomeRatio: emptyPatientWaiver.expenseIncomeRatio,
              expirationDt: emptyPatientWaiver.expirationDt
            }
          }else{
            waiverData = {
              scholarshipPercentage: waiverResponse.entity.scholarshipPercentage,
              fplRatio: waiverResponse.entity.fplRatio,
              expenseIncomeRatio: waiverResponse.entity.expenseIncomeRatio,
              expirationDt: moment().add(clientWaiverConfig?.expireDays || 0, 'days').toISOString()
            }

            if(!waiverResponse.entity.selectedScholarshipConfig){
              const selectedConfig = clientScholarshipsConfig.find((config) => {
                return config?.scholarshipPercentage == waiverResponse.entity?.scholarshipPercentage
              })
              await formik.setFieldValue('selectedScholarshipConfig', selectedConfig)
            }else{
              await formik.setFieldValue('selectedScholarshipConfig', waiverResponse.entity.selectedScholarshipConfig)
            }
          }
        }
      }

      const patientToSave = mapToSaveModel({
        ...valuesForSave,
        ...powerlyticsData
      }, zipCode4);

      const insuranceToSave = mapToInsuranceObj(valuesForSave);

      let userResponse;

      if (patientToSave?.contact?.email) {
        userResponse = await userService.handleCreateOrUpdateUser({
          userEmail: patientToSave.contact.email,
          clientId: Number(patientToSave.clientId),
          name: {
            firstName: patientToSave.contact.firstName,
            lastName: patientToSave.contact.lastName,
          },
        });
      }

      const patientResponse = await dispatch(savePatient({
        patient: {
          ...patientToSave,
          ...userResponse?.userId ? {userId: userResponse.userId} : {},
        },
      })).unwrap();

      await dispatch(savePatientCurrentInsurance({
        insurance: {
          ...insuranceToSave,
          patientId: patientResponse?.patientDetails?.patientId,
        },
      }));

      if(isClientConfiguredForFA){
        const savedPatientId = Number(
          patientResponse?.patientDetails?.patientId
        );
        await dispatch(savePatientWaiver({
          waiverId: resolveWaiverIdForSave(
            formik.values.waiverId,
            savedPatientId,
            selectedPatientWaiver?.patientId
          ),
          patientId: patientResponse?.patientDetails?.patientId,
          scholarshipPercentage: formik.values.scholarshipPercentage,
          fplRatio: formik.values.fplRatio,
          expenseIncomeRatio: formik.values.expenseIncomeRatio,
          expirationDt: formik.values.expirationDt,
          monthlyExpenses: formik.values.monthlyExpenses,
          householdSize: formik.values.householdSize,
          educationLevel: formik.values.educationLevel,
          ...waiverData
        }))
      }

      const savedPatientId = Number(
        patientResponse?.patientDetails?.patientId
      );
      if (!Number.isInteger(savedPatientId) || savedPatientId <= 0) {
        dispatch(showErrorStatus('Failed to save patient'));
        return {ok: false};
      }

      if (patientToSave.patientId === 0) {
        await formik.setFieldValue('patientId', savedPatientId);
        // Update URL after creating patient resource (skip when Verify continues in-place)
        if (!options?.skipNavigateOnCreate) {
          navigate(`/patients/${savedPatientId}`, {replace: true});
        }
      }
      return {ok: true, patientId: savedPatientId};
    } catch (e: any) {
      const message = e instanceof Error ? e.message : String(e);
      console.error(e);
      dispatch(showErrorStatus(message));
      return {ok: false};
    }

  };

  const {
    showRunAvailityButton,
    showAvailitySplitButton,
    showInlineFacilityMenuItems,
    availityFacilities,
    isLoadingAvailityFacilities,
    isRunningAvailityVOB,
    cancelAvailityVob,
    runAvailityVob,
    showUpdateInsuranceModal,
    isSavingInsurance,
    onUpdateInsuranceYes,
    onUpdateInsuranceNo,
    showFacilityPickerModal,
    openFacilityPicker,
    closeFacilityPicker,
    confirmFacilityPicker,
  } = useRunAvailityVob(formik.values, handleSave, async () => {
    shouldShowErrorMessage = true;
    const errors = await formik.validateForm();
    return Object.keys(errors).length === 0;
  });

  useEffect(() => {
    onAvailityRunningChange?.(isRunningAvailityVOB);
  }, [isRunningAvailityVOB, onAvailityRunningChange]);

  useEffect(() => {
    onAvailityCancelReady?.(cancelAvailityVob);
  }, [cancelAvailityVob, onAvailityCancelReady]);

  useEffect(() => {
    if (onFormValuesChange) {
      onFormValuesChange({
        clientId: formik.values.clientId || undefined,
        clientEmployerId: formik.values.clientEmployerId,
        payorId: formik.values.payorId,
        groupNumber: formik.values.groupNumber,
        memberId: formik.values.memberId,
      });
    }
  }, [formik.values.clientId, formik.values.clientEmployerId, formik.values.payorId, formik.values.groupNumber, formik.values.memberId]);

  const clientWaiverConfig = clientWaiversMap.get(formik.values.clientId!)
  const isClientConfiguredForFA = clientWaiverConfig?.isEnabled

  useEffect(()=>{
    const patientId = Number(formik.values.patientId) || 0;
    if(!isClientConfiguredForFA || patientId <= 0){
      return;
    }
    let cancelled = false;
    (async () => {
      const response = await patientService.getAllPatientWaivers(patientId);
      if (cancelled) {
        return;
      }
      if (response.hasErrors) {
        dispatch(showErrorStatus(response.errorMessage));
        return;
      }
      const list = Array.isArray(response.entity) ? response.entity : [];
      const waiver = list[0];
      if (waiver?.patientId && Number(waiver.patientId) !== patientId) {
        return;
      }
      dispatch(setPatientWaiver(waiver || emptyPatientWaiver));
    })();
    return () => {
      cancelled = true;
    };
  }, [isClientConfiguredForFA, formik.values.patientId, dispatch])

  const selectedEmploymentStatus = useMemo(() => {
    return employmentStatus.find((status) => status.employmentStatusId === formik.values.employmentStatusId)
  }, [formik.values.employmentStatusId])

  const saveButtonOnClick = async () => {

    shouldShowErrorMessage = true;
    const errors = await formik.validateForm();

    setIsSaving(true);

    if (Object.keys(errors).length === 0) {

      await handleSave();
    }
    setIsSaving(false);
  };

  return (
    <>
      <form onSubmit={formik.handleSubmit} noValidate style={{height: '100%'}}>
        {
          isPatientTabBlockingLoading ? (
              <Grid
                  container
                  justifyContent="center"
                  alignItems="center"
                  sx={{height: '100%'}}
              >
                <Grid item>
                  <CircularProgress />
                </Grid>
              </Grid>
          ) : (
              <>
                <div style={{paddingLeft: '18px', paddingTop: '10px'}}>
                  <Grid container spacing={2} style={{marginBottom: 12}}
                        justifyContent="space-between">
                    <Grid item xs={4} className="MuiTextField-root">
                      <FormControl fullWidth size="small">
                        <InputLabel id="client-select">Client</InputLabel>
                        <Select
                            labelId="client-select"
                            label="Client"
                            value={formik.values.clientId}
                            onChange={(e) => {
                              const selectedClientId = (e.target as HTMLInputElement).value;
                              formik.setFieldValue('clientId',
                                  selectedClientId);

                              //Clear out Employment Employer and Financial Assistance values
                              //since those are driven by client

                              formik.setFieldValue('clientEmployerId', emptyContactIncome.clientEmployerId)
                              formik.setFieldValue('fplRatio', emptyPatientWaiver.fplRatio)
                              formik.setFieldValue('expenseIncomeRatio', emptyPatientWaiver.expenseIncomeRatio)
                              formik.setFieldValue('scholarshipPercentage', emptyPatientWaiver.scholarshipPercentage)
                              formik.setFieldValue('expirationDt', emptyPatientWaiver.expirationDt)
                              formik.setFieldValue('selectedScholarshipConfig', null)
                            }}
                            style={{
                              width: '100%',
                            }}
                            renderValue={(selected) => {
                              return allClientsMap?.get(
                                      selected!)?.clientName ||
                                  'Select Client';
                            }}
                            disabled={!!formik.values.patientId}
                            className={`state-field ${formik.touched.clientId &&
                            Boolean(formik.errors.clientId) ?
                                'error-border' :
                                'required-fields'}`}
                            required
                            test-id="patient-tab-client-select"
                        >
                          {clients && clients.map((client: any) => (
                              <MenuItem key={client.clientId}
                                        value={client.clientId}>
                                {client.clientName}
                              </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid container item xs={6} justifyContent="flex-end">
                      <Grid item>
                        <Button onClick={() => {
                          Utils.resetPatientContexts(dispatch);
                          formik.resetForm();
                          formik.setFieldValue('patientId', 0);
                          formik.setFieldValue('waiverId', 0); 
                          // Clear the encounter before fetching the new estimate
                          dispatch(clearPatient());
                          dispatch(clearSelectedVob());
                          dispatch(clearPatientVobList());
                          dispatch(clearSelectedEstimate());
                          dispatch(clearSavedEstimatesList());
                          dispatch(clearFacilityPayers());
                          dispatch(clearRiskAssessment());
                          dispatch(clearSelectedEncounter());
                          dispatch(clearComments());
                          navigate(`/patients`,
                              {replace: true});
                        }}>
                          New
                        </Button>
                      </Grid>
                      <Grid item>
                        <Button
                            isSubmitButton={true}
                            onClick={async () => {
                              await saveButtonOnClick();
                            }}
                            disabled={isSaving || isRunningAvailityVOB}
                            test-id="patient-tab-save-2"
                            style={{
                              marginLeft: '1rem',
                            }}
                        >
                          {isSaving ? 'Saving...' : 'Save'}
                        </Button>
                      </Grid>
                    </Grid>
                  </Grid>
                  <div style={{paddingTop: 12}}>
                    <PersonalInfoSection
                        values={formik.values}
                        errors={formik.errors}
                        touched={formik.touched}
                        handleChange={formik.handleChange}
                        handleBlur={formik.handleBlur}
                        setFieldValue={formik.setFieldValue}
                    />
                  </div>
                </div>

                <CustomizeableAccordion
                    icon={<PersonIcon className="gray-icon"/>}
                    headerText="Demographics"
                    details={
                      <>
                        <ContactInfoSection
                            values={formik.values}
                            errors={formik.errors}
                            touched={formik.touched}
                            handleChange={formik.handleChange}
                            handleBlur={formik.handleBlur}
                            setFieldValue={formik.setFieldValue}
                        />
                        <AddressInfoSection
                            values={formik.values}
                            errors={formik.errors}
                            touched={formik.touched}
                            handleChange={formik.handleChange}
                            handleBlur={formik.handleBlur}
                            setFieldValue={formik.setFieldValue}
                        />
                      </>
                    }
                    isDefaultExpanded
                />
                <CustomizeableAccordion
                    icon={<FolderOpenIcon className="gray-icon"/>}
                    headerText="Current Insurance"
                    details={
                      <InsuranceInfoSection
                          formik={formik}
                          currentInsurancePayors={currentInsurancePayors}
                          isPayorsLoading={insurancePayorsListLoading}
                          showRunAvailityButton={showRunAvailityButton}
                          showAvailitySplitButton={showAvailitySplitButton}
                          showInlineFacilityMenuItems={showInlineFacilityMenuItems}
                          availityFacilities={availityFacilities}
                          isLoadingAvailityFacilities={isLoadingAvailityFacilities}
                          isRunningAvailityVOB={isRunningAvailityVOB}
                          onRunAvailityVob={runAvailityVob}
                          onOpenFacilityPicker={openFacilityPicker}
                      />
                    }
                    isDefaultExpanded
                />
                <CustomizeableAccordion
                    icon={<FolderOpenIcon className="gray-icon"/>}
                    headerText="Employment"
                    details={
                      <EmploymentInfoSection
                          formik={formik}
                          setCTPInitialValues={setCTPInitialValues}
                      />

                    }
                />
                {isClientConfiguredForFA && (
                    <CustomizeableAccordion
                        icon={<FolderOpenIcon className="gray-icon"/>}
                        headerText="Financial Assistance"
                        details={
                          <FinancialAssistance
                              formik={formik}
                              setWEInitialValues={setWEInitialValues}
                          />

                        }
                    />
                )}
                <Button
                    isSubmitButton={true}
                    onClick={async () => {
                      await saveButtonOnClick();
                    }}
                    disabled={isSaving}
                    style={{float: 'right', marginTop: '20px'}}
                    test-id="patient-tab-save"
                >
                  {isSaving ? 'Saving...' : 'Save'}
                </Button>
              </>
          )
        }

      </form>
      <UpdateInsuranceAfterVobDialog
          open={showUpdateInsuranceModal}
          isSaving={isSavingInsurance}
          onYes={onUpdateInsuranceYes}
          onNo={onUpdateInsuranceNo}
      />
      <SelectFacilityForAvailityDialog
          open={showFacilityPickerModal}
          facilities={availityFacilities}
          isRunning={isRunningAvailityVOB}
          onCancel={closeFacilityPicker}
          onConfirm={confirmFacilityPicker}
      />
    </>
  );
};

const mapToInsuranceObj = (formValues: PatientFormValues): PatientCurrentInsurance => {
  return {
    patientCurrentInsuranceId: formValues.patientCurrentInsuranceId,
    patientId: formValues.patientId,
    payorId: formValues.payorId,
    payorPlanId: formValues.payorPlanId,
    subscriberName: formValues.subscriberName,
    subscriberRelation: formValues.subscriberRelation,
    memberId: formValues.memberId,
    groupNumber: formValues.groupNumber,
  };
};

const mapToSaveModel = (formValues: PatientFormValues, zipCode4?: string) => {

  const stateInfo = stateAbbrevLookup(formValues.state);
  const state = {
    stateId: stateInfo.id,
    stateCode: formValues.state,
    stateName: stateInfo.name,
  } as State;

  const address: PrimaryAddress = {
    streetAddress1: formValues.streetAddress1,
    streetAddress2: formValues.streetAddress2,
    city: formValues.city,
    state: state,
    zipCode: formValues.zipCode,
    zipCode4: zipCode4 ? zipCode4 : formValues.zipCode4,
    addressType: 'Primary',
  };

  const contactIncome = {
    yearlySalary: formValues.yearlySalary,
    employer: formValues.employer,
    yearsAtEmployer: formValues.yearsAtEmployer,
    verificationScore: formValues.verificationScore,
    retirementIncome: formValues.retirementIncome,
    capacityToPay: formValues.capacityToPay,
    disposableIncome: formValues.disposableIncome,
    businessIncome: formValues.businessIncome,
    wealthIncome: formValues.wealthIncome,
    wagesAndEarnings: formValues.wagesAndEarnings,
    clientEmployerId: formValues.clientEmployerId,
    employmentStatusId: formValues.employmentStatusId

  } as ContactIncome;

  const contact = {
    title: formValues.title,
    preferredName: formValues.preferredName,
    firstName: formValues.firstName,
    lastName: formValues.lastName,
    middleName: formValues.middleName,
    primaryAddress: address,
    email: formValues.email,
    primPhoneNum: formValues.firstPhone,
    primPhoneType: formValues.isFirstPhoneMobile
        ? PhoneType.mobile
        : PhoneType.home,
    secPhoneNum: formValues.secondPhone,
    secPhoneType: formValues.isSecondPhoneMobile
        ? PhoneType.mobile
        : PhoneType.home,
    leaveVMFl: formValues.leaveVoicemail,
    smsConsentFl: formValues.receiveSMSMessages,
    birthDate: formValues.dateOfBirth,
    contactIncome: contactIncome,
  } as PrimaryContact;

  return {
    contact,
    patientId: formValues?.patientId,
    clientId: formValues.clientId,
  };
};

